import { createContext, useContext } from 'react';
import useCustomer from '@adapters/bigcommerce/hooks/customer/useCustomer';
import {
  getGroupKeyByCustomerGroupId,
  PriceGroupKey,
} from '@utils/selectPriceByGroup';

/**
 * SSR-resolved pricing context, seeded from the customer cookie in
 * `getServerSideProps`. `CustomerContext` defaults `customer` to `null` on the
 * server and the first client render, so without this the group would resolve
 * to `guest` until auth hydrates — flipping the price sort replica and filter
 * attribute after mount and forcing an extra Algolia query for logged-in
 * customers. Seeding the resolved group here keeps SSR and the first client
 * render in sync so the attribute never flips on a hard reload.
 */
export const SsrPriceGroupContext = createContext<PriceGroupKey>('guest');

/**
 * Resolves the active pricing-context key (guest/member/employee/family/acmg/
 * caffr) used to pick the price-sort replica and the price-filter attribute.
 *
 * Prefers the live customer group once `CustomerContext` has hydrated; until
 * then it falls back to the SSR-resolved group from `SsrPriceGroupContext`
 * (`guest` when nothing was provided). On a client-side login/logout the
 * customer group takes over on the next render.
 */
export const usePriceGroupKey = (): PriceGroupKey => {
  const { customer } = useCustomer();
  const ssrPriceGroupKey = useContext(SsrPriceGroupContext);

  return customer?.customerGroupId
    ? getGroupKeyByCustomerGroupId(customer.customerGroupId)
    : ssrPriceGroupKey;
};

export default usePriceGroupKey;
